import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Suspense, type ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { AppErrorFallback } from './AppErrorFallback'
import { AuthProvider } from '@/features/auth/session/AuthProvider'
import { queryClient } from '@/shared/api/query-client'
import { PageLoader } from '@/shared/components/PageLoader'
import { ReactionPauseModal } from '@/shared/components/ReactionPauseModal'
import { ToastProvider } from '@/shared/components/toast'
import { ThemeProvider } from '@/shared/theme/ThemeProvider'

/**
 * App-level composition root: last-resort error boundary, server-state
 * cache, and the Suspense boundary that covers the initial translation
 * load. The devtools render only in development builds.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={AppErrorFallback}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ToastProvider>
              <Suspense fallback={<PageLoader className="min-h-screen" />}>
                {children}
                <ReactionPauseModal />
              </Suspense>
            </ToastProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
